import {
  IClarifyingQuestion,
  IConfirmServiceVisitRequest,
  ICreateServiceRequestRequest,
  IQuoteServiceRequestRequest,
  IServiceRequest,
  IServiceRequestClarification,
  IServiceRequestPhoto,
  IServiceRequestSummary,
} from '../interfaces/heavyDutyMachinery/IServiceRequest';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { apiService, apiUploadService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import { getLocalStorageKey } from '../helpers/localStorageHelper';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** The customer describing a job. No dates: that is the whole point of the flow. */
export const createServiceRequest = async (request: ICreateServiceRequestRequest): Promise<IServiceRequest> => {
  const response = await apiService<IServiceRequest>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUESTS,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to send the request');
};

export const getMyServiceRequests = async (status?: string): Promise<IServiceRequestSummary[]> => {
  const response = await apiService<IServiceRequestSummary[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUESTS_MINE,
    method: HTTP_METHODS.GET,
    params: status ? { status } : undefined,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load requests');
};

/** The professional's inbox. The server puts what is unanswered first. */
export const getIncomingServiceRequests = async (status?: string): Promise<IServiceRequestSummary[]> => {
  const response = await apiService<IServiceRequestSummary[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUESTS_INCOMING,
    method: HTTP_METHODS.GET,
    params: status ? { status } : undefined,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load requests');
};

export const getServiceRequest = async (id: string): Promise<IServiceRequest> => {
  const response = await apiService<IServiceRequest>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the request');
};

/** Professional-only. Putting a price on the table, and revising it -- same call both times. */
export const quoteServiceRequest = async (id: string, request: IQuoteServiceRequestRequest): Promise<IServiceRequest> => {
  const response = await apiService<IServiceRequest>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_QUOTE(id),
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to send the quote');
};

/**
 * The customer saying yes to the PRICE. Nothing is reserved and nothing is
 * charged here -- the booking appears when the professional fixes the visit.
 * The liability waiver rides along because this is the moment they commit.
 */
export const acceptServiceRequest = async (id: string): Promise<IServiceRequest> => {
  const response = await apiService<IServiceRequest>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_ACCEPT(id),
    method: HTTP_METHODS.POST,
    body: { liabilityWaiverAccepted: true },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to accept');
};

export const declineServiceRequest = async (id: string, reason?: string): Promise<IServiceRequest> => {
  const response = await apiService<IServiceRequest>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_DECLINE(id),
    method: HTTP_METHODS.POST,
    body: { reason },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to decline');
};

export const cancelServiceRequest = async (id: string, reason?: string): Promise<IServiceRequest> => {
  const response = await apiService<IServiceRequest>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_CANCEL(id),
    method: HTTP_METHODS.POST,
    body: { reason },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to withdraw');
};

/**
 * Professional-only, and the moment everything that was deliberately not
 * happening happens: the diary is blocked, the booking exists and the
 * customer is asked to pay.
 *
 * No clientSecret comes back on purpose -- that secret confirms a payment on
 * the CUSTOMER's card, and the caller here is the professional.
 */
export const confirmServiceVisit = async (id: string, request: IConfirmServiceVisitRequest): Promise<IBooking> => {
  const response = await apiService<IBooking>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_FROM_SERVICE_REQUEST(id),
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to confirm the visit');
};

/**
 * What is worth asking about a job somebody has just described.
 *
 * Returned as an outcome rather than thrown, same as the quote assistant and
 * for the same reason: "not switched on here" is an ordinary state of the
 * platform, and the screen answers it by putting the feature away rather than
 * showing somebody a red message about something they did not do wrong.
 *
 * An empty list is a real answer -- the description already said enough.
 */
export type AssistOutcome<T> = { ok: true; value: T } | { ok: false; unavailable: boolean; error?: string };

const SERVICE_UNAVAILABLE = 503;

export const askClarifyingQuestions = async (
  description: string,
  trade?: string,
  serviceId?: string
): Promise<AssistOutcome<IClarifyingQuestion[]>> => {
  const response = await apiService<{ questions: IClarifyingQuestion[] }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_ASSIST_QUESTIONS,
    method: HTTP_METHODS.POST,
    // serviceId is how the server finds out whose listing this is, and so
    // what THIS professional always wants to know (their assistant guide).
    // Without it the assistant still reads the job perfectly well.
    body: { description, trade, serviceId: serviceId ? Number(serviceId) : undefined },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return { ok: true, value: response.data.questions ?? [] };
  return { ok: false, unavailable: response.status === SERVICE_UNAVAILABLE, error: response.error };
};

/** The short version the professional reads first. Only ever made of what the customer wrote or answered. */
export const summariseJob = async (
  description: string,
  answers: IServiceRequestClarification[]
): Promise<AssistOutcome<string>> => {
  const response = await apiService<{ summary: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_ASSIST_SUMMARY,
    method: HTTP_METHODS.POST,
    body: { description, answers },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return { ok: true, value: response.data.summary ?? '' };
  return { ok: false, unavailable: response.status === SERVICE_UNAVAILABLE, error: response.error };
};

/**
 * One photo of the job, uploaded on its own -- before the request exists,
 * because the customer is still writing it. It hangs unattached under their
 * name until they press send.
 */
export const uploadServiceRequestPhoto = async (file: File): Promise<IServiceRequestPhoto> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiUploadService<IServiceRequestPhoto>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_PHOTOS,
    formData,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to upload the photo');
};

/** Only works while it is still unattached -- once sent it is part of what the professional priced from. */
export const deleteServiceRequestPhoto = async (photoId: string): Promise<void> => {
  const response = await apiService<void>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_PHOTO(photoId),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete the photo');
};

/**
 * Fetched through the authenticated endpoint and held as a local object URL
 * -- same convention as the dispute evidence and the albarán's own photos,
 * for the same reason: never reachable from a guessable URL.
 */
export const getServiceRequestPhotoObjectUrl = async (photoId: string): Promise<string> => {
  const accessToken = getLocalStorageKey('accessToken');
  const url = API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_REQUEST_PHOTO(photoId);
  const response = await fetch(`${heavyDutyMachineryBaseUrl}${url}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!response.ok) throw new Error(`Failed to download photo (HTTP ${response.status})`);
  return window.URL.createObjectURL(await response.blob());
};
